import * as React from 'react';
import styled from 'styled-components';
import { applyMarkdownCommand, MarkdownCommand, MARKDOWN_COMMANDS } from './markdown-commands';

export interface MarkdownEditorProps {
  id?: string;
  label?: React.ReactNode;
  error?: string | boolean;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  minHeight?: number;
  s?: number;
  className?: string;
}

const Field = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  opacity: 0.75;
`;

const Frame = styled.div<{ $invalid: boolean }>`
  border: 1px solid ${({ $invalid }) => ($invalid ? '#e53935' : 'rgba(128, 128, 128, 0.45)')};
  border-radius: 6px;
  overflow: hidden;
  background: rgba(127, 127, 127, 0.06);
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.35);
  background: rgba(127, 127, 127, 0.08);
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 28px;
  padding: 0 7px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font-size: 0.85rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(127, 127, 127, 0.28);
  }

  &:focus-visible {
    outline: 2px solid rgba(127, 127, 127, 0.6);
    outline-offset: -2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Separator = styled.span`
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background: rgba(128, 128, 128, 0.4);
`;

const Editor = styled.textarea<{ $minHeight: number }>`
  display: block;
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight}px;
  box-sizing: border-box;
  margin: 0;
  padding: 12px 14px;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.55;
  resize: vertical;

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    opacity: 0.5;
  }
`;

const Hint = styled.p`
  margin: 6px 0 0;
  font-size: 0.72rem;
  opacity: 0.55;
`;

const ErrorText = styled.p`
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #e53935;
`;

const MarkdownEditor = ({
  id,
  label,
  error,
  placeholder,
  value,
  onChange,
  disabled,
  minHeight = 180,
  s = 12,
  className,
}: MarkdownEditorProps) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const hasError = Boolean(error);
  const errorMessage = typeof error === 'string' ? error : undefined;
  const text = value || '';

  const runCommand = (command: MarkdownCommand) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const result = applyMarkdownCommand(command, text, textarea.selectionStart, textarea.selectionEnd);

    onChange?.(result.value);

    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  return (
    <Field className={`col s${s} ${className || ''}`.trim()}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Frame $invalid={hasError}>
        <Toolbar role="toolbar" aria-label="Formatação do texto">
          {MARKDOWN_COMMANDS.map((command, index) =>
            command.name === 'separator' ? (
              <Separator key={`sep-${index}`} aria-hidden="true" />
            ) : (
              <ToolbarButton
                key={command.name}
                type="button"
                title={command.title}
                aria-label={command.title}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => runCommand(command)}
              >
                {command.icon}
              </ToolbarButton>
            )
          )}
        </Toolbar>
        <Editor
          id={id}
          ref={textareaRef}
          $minHeight={minHeight}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          onChange={(event) => onChange?.(event.target.value)}
        />
      </Frame>
      {errorMessage ? (
        <ErrorText>{errorMessage}</ErrorText>
      ) : (
        <Hint>Use os botões acima ou escreva em Markdown: **negrito**, _itálico_, - lista</Hint>
      )}
    </Field>
  );
};

MarkdownEditor.displayName = 'MarkdownEditor';

export { MarkdownEditor };
